import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { PermissionGate } from "@/components/PermissionGate";
import { deriveJobState, checkFinancialIntegrity } from "@/data/jobStateDerived";
import { getStageConfig } from "@/data/workflowEngine";
import { validateStatusWrite } from "@/data/workflowAudit";
import { Link, useSearchParams } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { CloseoutService, type ReviewerChecklist } from "@/services/closeout.service";
import type { CloseoutQueueItem } from "@/services/closeout.service";
import { IssueService } from "@/services/issue.service";
import { QAService } from "@/services/qa.service";
import { generateScopeOfWork } from "@/data/scopeOfWork";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { StorageThumbnailImage } from "@/components/media/StorageThumbnailImage";
import { Progress } from "@/components/ui/progress";
import { formatShortDate } from "@/lib/dateFormat";
import { toast } from "sonner";
import type { Job, CloseoutStatus, Issue } from "@/data/types";
import { OUTCOME_OPTIONS, SETTLEMENT_LABELS } from "@/data/installerWrapEngine";
import type { InstallerWrapRecord } from "@/data/installerWrapEngine";
import { deriveFinancialClearance } from "@/data/financialClearance";
import { getFinancialSummary as getCollectionSummary } from "@/data/invoiceEngine";
import { evaluateCloseoutGate } from "@/data/serviceCallPrevention";
import { getCloseoutSignoff, validateCloseoutSignoff } from "@/data/closeoutSignoff";
import { getAttachments } from "@/services/attachment.service";
import { resolveImportPhotoUrls } from "@/data/import/importPhotoUrls";
import { createSignedStorageFullImageUrl } from "@/lib/storageImageUrls";
import { useExecutionProofs } from "@/hooks/useExecutionProofs";
import {
  Search, CheckCircle, XCircle, Clock, AlertTriangle,
  ArrowRight, Filter, FileCheck, Camera, Receipt,
  Shield, Eye, ClipboardCheck, ArrowLeft, Wrench,
  Image, Columns2, ListChecks, RotateCcw, Plus, DollarSign,
  CheckCircle2, Send, Star, Users, MessageSquare,
  ThumbsUp, ThumbsDown, ExternalLink, Sunset, ChevronDown, ChevronUp,
} from "lucide-react";

const CLOSEOUT_STAGES: CloseoutStatus[] = [
  "Not Started", "Install Complete", "QA Passed", "Final Photos Complete",
  "Issues Resolved", "Ready for Closeout", "Closed",
];

function getJobDisplayRef(job: { id: string; jobNumber?: unknown }): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

function getInstallerDisplayName(job: Pick<Job, "assignedCrew" | "assignedSubcontractor">): string {
  return job.assignedCrew || job.assignedSubcontractor || "Unassigned";
}

function getCollectionPaymentLabel(paymentStatus: string): string {
  const labels: Record<string, string> = {
    no_invoice_plan: "No invoice plan",
    deposit_pending: "Deposit pending",
    deposit_paid: "Deposit received",
    progress_pending: "Progress pending",
    final_pending: "Final pending",
    paid_in_full: "Paid in Full",
    overdue: "Overdue",
  };
  return labels[paymentStatus] ?? paymentStatus;
}

function isRenderablePhoto(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:") || v.startsWith("blob:") || v.startsWith("/")) return true;
  return /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(v);
}

function dedupePhotoUrls(urls: string[]): string[] {
  const seen = new Set<string>();
  return urls.filter((url) => {
    if (!isRenderablePhoto(url) || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

/* ═══════════════════════════════════════════
   Main Page
   ═══════════════════════════════════════════ */

export default function ProjectCloseout() {
  const [searchParams] = useSearchParams();
  const { jobs: appJobs, issues, credits } = useAppData();
  const [queueItems, setQueueItems] = useState<CloseoutQueueItem[]>([]);
  const queueRequestInFlightRef = useRef(false);
  const queueBootstrappedRef = useRef(false);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState<CloseoutStatus | "All">("All");
  const [detailJobId, setDetailJobId] = useState<string | null>(null);
  const queueJobs = useMemo(() => queueItems.map((item) => item.job), [queueItems]);
  const jobs = queueJobs.length > 0 ? queueJobs : appJobs;

  const loadQueue = useCallback(async () => {
    if (queueRequestInFlightRef.current) return;
    queueRequestInFlightRef.current = true;
    setLoadingQueue(true);
    const { data, error } = await CloseoutService.getQueue();
    if (error) {
      toast.error("Failed to load closeout queue");
    } else {
      setQueueItems(data ?? []);
    }
    setLoadingQueue(false);
    queueRequestInFlightRef.current = false;
  }, []);

  useEffect(() => {
    if (queueBootstrappedRef.current) return;
    queueBootstrappedRef.current = true;
    void loadQueue();
  }, [loadQueue]);

  const relevantJobs = useMemo(() => jobs.filter(
    (j) =>
      j.closeoutStatus !== "Not Started" ||
      (getStageConfig(j.currentStage)?.order ?? 0) >= 90
  ), [jobs]);

  const filtered = useMemo(() => relevantJobs.filter((j) => {
    const matchSearch =
      j.customerName.toLowerCase().includes(search.toLowerCase()) ||
      j.id.toLowerCase().includes(search.toLowerCase()) ||
      getJobDisplayRef(j).toLowerCase().includes(search.toLowerCase());
    const matchStage = filterStage === "All" || j.closeoutStatus === filterStage;
    return matchSearch && matchStage;
  }), [relevantJobs, search, filterStage]);
  const pagination = usePagination(filtered, 20);
  useEffect(() => {
    pagination.reset();
  }, [search, filterStage]);

  const stageCounts = CLOSEOUT_STAGES.map((s) => ({
    stage: s,
    count: relevantJobs.filter((j) => j.closeoutStatus === s).length,
  }));

  const awaitingCloseout = relevantJobs.filter(j => j.closeoutStatus === "Ready for Closeout");
  const qaReview = relevantJobs.filter(j => (getStageConfig(j.currentStage)?.order ?? 0) === 90 && j.closeoutStatus !== "QA Passed" && j.closeoutStatus !== "Closed");
  const incompleteDocs = relevantJobs.filter(
    j => !j.finalDocumentationComplete && j.closeoutStatus !== "Not Started" && j.closeoutStatus !== "Closed"
  );
  const paymentPending = relevantJobs.filter(j => {
    if (j.closeoutStatus === "Closed") return false;
    const plan = j.financials?.paymentPlan;
    if (!plan) return false;
    return plan.lineItems.some(li => li.dueAt === "post_install" && li.status !== "paid" && li.status !== "waived");
  });

  const detailJob = detailJobId ? jobs.find(j => j.id === detailJobId) : null;

  useEffect(() => {
    const targetJobId = searchParams.get("job");
    if (!targetJobId) return;
    if (!jobs.some((j) => j.id === targetJobId)) return;
    setDetailJobId((prev) => prev ?? targetJobId);
  }, [searchParams, jobs]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
          <FileCheck className="h-5 w-5" /> QA / Closeout
        </h1>
        <p className="text-sm text-muted-foreground">
          Post-install verification, quality assurance, and final project closure
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "QA Review", count: qaReview.length, icon: ClipboardCheck, color: "text-primary" },
          { label: "Awaiting Closeout", count: awaitingCloseout.length, icon: Clock, color: "text-warning dark:text-warning" },
          { label: "Incomplete Docs", count: incompleteDocs.length, icon: Camera, color: "text-destructive" },
          { label: "Fully Closed", count: relevantJobs.filter(j => j.closeoutStatus === "Closed").length, icon: CheckCircle, color: "text-success dark:text-success" },
        ].map((kpi) => (
          <Card key={kpi.label} className="shadow-none">
            <CardContent className="p-4 flex items-center gap-3">
              <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
              <div>
                <p className="text-2xl font-bold">{kpi.count}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{kpi.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pipeline filter */}
      <Card className="shadow-none">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <button
              onClick={() => setFilterStage("All")}
              className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                filterStage === "All" ? "bg-primary text-primary-foreground border-primary" : "bg-muted/50 text-muted-foreground hover:bg-muted"
              }`}
            >
              All ({relevantJobs.length})
            </button>
            {stageCounts.filter((s) => s.count > 0).map((s) => (
              <button
                key={s.stage}
                onClick={() => setFilterStage(s.stage)}
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  filterStage === s.stage ? "bg-primary text-primary-foreground border-primary" : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
              >
                {s.stage} ({s.count})
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by job or customer..."
          className="pl-9 h-9 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* QA Review Queue (priority) */}
      {qaReview.length > 0 && (
        <Card className="shadow-none border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-primary" />
              QA Review Queue ({qaReview.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {qaReview.map(job => (
              <div
                key={job.id}
                onClick={() => setDetailJobId(job.id)}
                className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Eye className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{getJobDisplayRef(job)} — {job.customerName}</p>
                    <p className="text-[10px] text-muted-foreground">{job.projectType} · {getInstallerDisplayName(job)}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="text-[10px] h-7">
                  <Eye className="h-3 w-3 mr-1" /> Review
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Payment Pending Queue */}
      {paymentPending.length > 0 && (
        <Card className="shadow-none border-warning/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-warning" />
              Payment Pending ({paymentPending.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {paymentPending.map(job => {
              const balance = job.financials?.paymentPlan?.lineItems
                .filter(li => li.dueAt === "post_install" && li.status !== "paid" && li.status !== "waived")
                .reduce((s, li) => s + li.amount, 0) || 0;
              return (
                <Link key={job.id} to={`/jobs/${job.id}`} className="block">
                  <div className="flex items-center justify-between p-3 rounded-lg border border-warning/20 bg-warning/5 hover:bg-warning/10 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-warning/20 flex items-center justify-center">
                        <DollarSign className="h-4 w-4 text-warning" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold">{getJobDisplayRef(job)} — {job.customerName}</p>
                        <p className="text-[10px] text-muted-foreground">${balance.toLocaleString()} balance due</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="text-[10px] h-7">
                      <DollarSign className="h-3 w-3 mr-1" /> Collect
                    </Button>
                  </div>
                </Link>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Main table */}
      <Card className="shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Closeout Tracker ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingQueue && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-b">Loading closeout queue...</div>
          )}
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Job</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Customer</th>
                  <th className="text-right py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden sm:table-cell">Revenue</th>
                  <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Closeout</th>
                  <th className="text-center py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden sm:table-cell">QA</th>
                  <th className="text-center py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden sm:table-cell">Docs</th>
                  <th className="text-center py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden md:table-cell">Issues</th>
                  <th className="text-center py-2.5 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden md:table-cell">Credits</th>
                  <th className="py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {pagination.paged.map((job) => {
                  const openIssues = issues.filter(i => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));
                  return (
                    <tr key={job.id} className="border-b hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => setDetailJobId(job.id)}>
                      <td className="py-2.5 px-3 font-mono font-semibold text-primary">{getJobDisplayRef(job)}</td>
                      <td className="py-2.5 px-3">{job.customerName}</td>
                      <td className="py-2.5 px-3 text-right hidden sm:table-cell font-semibold">
                        {job.financials?.contractValue
                          ? <span className={job.closeoutStatus === "Closed" ? "text-success dark:text-success" : "text-foreground"}>
                              ${job.financials.contractValue.toLocaleString()}
                            </span>
                          : <span className="text-muted-foreground/40">—</span>}
                      </td>
                      <td className="py-2.5 px-3"><StatusBadge type="closeout" value={job.closeoutStatus} /></td>
                      <td className="py-2.5 px-3 text-center hidden sm:table-cell">
                        {job.qaSubmitted ? <CheckCircle className="h-4 w-4 text-success mx-auto" /> : <XCircle className="h-4 w-4 text-muted-foreground/40 mx-auto" />}
                      </td>
                      <td className="py-2.5 px-3 text-center hidden sm:table-cell">
                        {job.finalDocumentationComplete ? <CheckCircle className="h-4 w-4 text-success mx-auto" /> : <XCircle className="h-4 w-4 text-muted-foreground/40 mx-auto" />}
                      </td>
                      <td className="py-2.5 px-3 text-center hidden md:table-cell">
                        {openIssues.length > 0 ? <span className="text-destructive font-bold">{openIssues.length}</span> : <CheckCircle className="h-4 w-4 text-success mx-auto" />}
                      </td>
                      <td className="py-2.5 px-3 hidden md:table-cell text-center">
                        {(() => {
                          const jobCredits = credits.filter(c => c.jobId === job.id);
                          if (jobCredits.length === 0) return <span className="text-muted-foreground/40">—</span>;
                          const total = jobCredits.reduce((s, c) => s + c.creditAmount, 0);
                          return <span className="inline-flex items-center gap-1 text-warning dark:text-warning font-medium"><Receipt className="h-3 w-3" />${total.toLocaleString()}</span>;
                        })()}
                      </td>
                      <td className="py-2.5 px-3">
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">No jobs match current filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <PaginationBar {...pagination} />

      {/* Detail Dialog */}
      {detailJob && (
        <QADetailDialog
          job={detailJob}
          open={!!detailJobId}
          onOpenChange={(o) => !o && setDetailJobId(null)}
          onMutated={loadQueue}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   QA Detail Dialog
   ═══════════════════════════════════════════ */

const QA_ITEMS = [
  { key: "materials", label: "Materials installed correctly" },
  { key: "defects", label: "No visible defects" },
  { key: "finish", label: "Clean finish — no debris, marks, or damage" },
  { key: "scope", label: "Full scope of work completed" },
  { key: "photos", label: "All required photos captured" },
];
const qaChecklistDefaults = Object.fromEntries(QA_ITEMS.map((item) => [item.key, false])) as Record<string, boolean>;
const qaChecklistAllTrue = Object.fromEntries(QA_ITEMS.map((item) => [item.key, true])) as Record<string, boolean>;

export function QADetailDialog({ job, open, onOpenChange, onMutated }: { job: Job; open: boolean; onOpenChange: (o: boolean) => void; onMutated?: () => void | Promise<void> }) {
  const { jobs, setJobs, issues, setIssues, installerWrapRecords, quotes } = useAppData();
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [serviceNotes, setServiceNotes] = useState("");
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [freshJobIssues, setFreshJobIssues] = useState<import("@/data/types").Issue[] | null>(null);
  const [executionProofPhotoUrls, setExecutionProofPhotoUrls] = useState<string[]>([]);
  const [jobPhotoUrls, setJobPhotoUrls] = useState<string[]>([]);
  const [quotePhotoUrls, setQuotePhotoUrls] = useState<string[]>([]);
  const [qaPhotoUrls, setQaPhotoUrls] = useState<string[]>([]);
  const executionLedger = useExecutionProofs({ appJobId: job.id, enabled: open });
  const collection = useMemo(() => getCollectionSummary(job), [job]);
  const isAlreadyClosed = job.closeoutStatus === "Closed";
  const closeoutDraftKey = useMemo(() => `closeout-qa-review:${job.id}`, [job.id]);

  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(closeoutDraftKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { checklist?: Record<string, boolean>; notes?: string };
        setChecklist({
          ...qaChecklistDefaults,
          ...(parsed.checklist ?? {}),
        });
        setNotes(typeof parsed.notes === "string" ? parsed.notes : "");
        return;
      }
    } catch {
      // ignore malformed draft and continue with defaults
    }

    // Closed jobs default to a fully-verified checklist view.
    setChecklist(isAlreadyClosed ? { ...qaChecklistAllTrue } : { ...qaChecklistDefaults });
    setNotes(job.closeoutNotes ?? "");
  }, [open, closeoutDraftKey, isAlreadyClosed, job.closeoutNotes]);

  useEffect(() => {
    if (!open) return;
    localStorage.setItem(
      closeoutDraftKey,
      JSON.stringify({
        checklist,
        notes,
        updatedAt: Date.now(),
      }),
    );
  }, [open, closeoutDraftKey, checklist, notes]);

  // Refresh issues from DB every time the dialog opens — context may be stale
  // if issues were created after the last AppDataContext load.
  useEffect(() => {
    if (!open) return;
    setFreshJobIssues(null);
    IssueService.getByJobNumber(job.id).then(({ data }) => {
      if (!data) return;
      setFreshJobIssues(data);
      // Sync context so other consumers (closeout gate, issue counts) stay current
      setIssues(prev => [...prev.filter(i => i.jobId !== job.id), ...data]);
    });
  }, [open, job.id]);

  // Use fresh DB issues when available; fall back to stale context while loading
  const jobIssues = freshJobIssues ?? issues.filter(i => i.jobId === job.id);
  const openIssues = jobIssues.filter(i => i.status === "Open" || i.status === "In Progress");
  const allChecked = QA_ITEMS.every(item => checklist[item.key]);
  const linkedQuoteId = useMemo(() => {
    const src = quotes.find((q) => q.convertedJobId === job.id || q.linkedJobId === job.id);
    return src?.id ?? null;
  }, [quotes, job.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const imageProofs = executionLedger.proofs.filter((proof) =>
        String(proof.mime_type ?? "").startsWith("image/"),
      );

      if (imageProofs.length === 0) {
        if (!cancelled) setExecutionProofPhotoUrls([]);
        return;
      }

      try {
        const urls = await Promise.all(
          imageProofs.map(async (proof) => {
            const rawPath = String(proof.storage_path ?? "").trim();
            if (rawPath) {
              const signedPathUrl = await createSignedStorageFullImageUrl(rawPath, {
                bucket: "execution-proofs",
              }).catch(() => "");
              if (signedPathUrl) return signedPathUrl;
            }
            return (await executionLedger.getReadUrl(proof.id)) ?? "";
          }),
        );
        if (!cancelled) setExecutionProofPhotoUrls(dedupePhotoUrls(urls.filter(Boolean)));
      } catch {
        if (!cancelled) setExecutionProofPhotoUrls([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [executionLedger.proofs, executionLedger.getReadUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [jobAttachments, resolvedImportRefs] = await Promise.all([
          getAttachments("job", job.id),
          resolveImportPhotoUrls(Array.isArray(job.photos) ? job.photos : []),
        ]);

        const attachmentPhotoUrls = await Promise.all(
          jobAttachments
            .filter((record: any) => (record.attachmentType ?? record.attachment_type) === "photo")
            .map((record: any) => {
              const rawPath = String(record.filePath ?? record.file_path ?? "").trim();
              if (!rawPath) return Promise.resolve("");
              return createSignedStorageFullImageUrl(rawPath, { bucket: "job-photos" }).catch(() => "");
            }),
        );

        const urls = dedupePhotoUrls([
          ...attachmentPhotoUrls.filter(Boolean),
          ...resolvedImportRefs,
        ]);

        if (!cancelled) setJobPhotoUrls(urls);
      } catch {
        if (!cancelled) setJobPhotoUrls([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [job.id, job.photos]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!linkedQuoteId) {
        if (!cancelled) setQuotePhotoUrls([]);
        return;
      }
      try {
        const records = await getAttachments("quote", linkedQuoteId);
        const photoRecords = records.filter((r: any) => r.attachment_type === "photo" || r.attachmentType === "photo");
        const urls = await Promise.all(
          photoRecords.map(async (r: any) => {
            const rawPath = String(r.file_path ?? r.filePath ?? "").trim();
            if (!rawPath) return "";
            return createSignedStorageFullImageUrl(rawPath, { bucket: "job-documents" }).catch(() => "");
          }),
        );
        if (!cancelled) setQuotePhotoUrls(dedupePhotoUrls(urls.filter(Boolean)));
      } catch {
        if (!cancelled) setQuotePhotoUrls([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkedQuoteId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await QAService.getByJobNumber(job.id);
        if (error || !data) {
          if (!cancelled) setQaPhotoUrls([]);
          return;
        }
        const urls = data.flatMap((submission) => Object.values(submission.photos ?? {}));
        if (!cancelled) setQaPhotoUrls(dedupePhotoUrls(urls));
      } catch {
        if (!cancelled) setQaPhotoUrls([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [job.id]);

  // Scope comparison
  const soldScope = useMemo(() => {
    if (job.projectConfig) return generateScopeOfWork(job.projectConfig);
    return { sections: [{ title: "Scope", items: [job.scopeSummary || job.projectType] }], summary: job.scopeSummary || job.projectType };
  }, [job]);

  const closeoutSignoff = useMemo(() => getCloseoutSignoff(job), [job]);
  const closeoutSignoffState = useMemo(
    () => validateCloseoutSignoff(closeoutSignoff),
    [closeoutSignoff],
  );

  // Photo categories from existing job data
  const allPhotoUrls = useMemo(() => {
    const base = [
      ...executionProofPhotoUrls,
      ...jobPhotoUrls,
      ...jobIssues.map((i) => i.photo),
      ...qaPhotoUrls,
      ...quotePhotoUrls,
    ].filter(isRenderablePhoto);
    return dedupePhotoUrls(base);
  }, [executionProofPhotoUrls, jobPhotoUrls, jobIssues, qaPhotoUrls, quotePhotoUrls]);

  const supplementalPhotoUrls = useMemo(() => {
    if (executionProofPhotoUrls.length === 0) return allPhotoUrls;
    const phaseProofSet = new Set(executionProofPhotoUrls);
    return allPhotoUrls.filter((url) => !phaseProofSet.has(url));
  }, [allPhotoUrls, executionProofPhotoUrls]);

  const photoCategories = useMemo(() => {
    const photos = allPhotoUrls;
    // Simulate categorization based on available photos
    return {
      before: photos.filter((_, i) => i % 3 === 0),
      during: photos.filter((_, i) => i % 3 === 1),
      after: photos.filter((_, i) => i % 3 === 2),
      total: photos.length,
    };
  }, [allPhotoUrls]);

  const handleApprove = async () => {
    if (isAlreadyClosed) {
      toast.info("This job is already closed.");
      onOpenChange(false);
      return;
    }
    if (!allChecked) {
      toast.error("All QA checklist items must be verified before approval");
      return;
    }

    if (!closeoutSignoffState.valid) {
      toast.error(`Cannot close — ${closeoutSignoffState.blockers[0]}`, {
        description:
          closeoutSignoffState.blockers.length > 1
            ? `+${closeoutSignoffState.blockers.length - 1} more sign-off blocker(s)`
            : undefined,
      });
      return;
    }

    // Fetch fresh issues from DB — context may be stale if issues were created
    // after the last AppDataContext load (QA form submissions, manual issue creation, etc.)
    const { data: freshIssues } = await IssueService.getByJobNumber(job.id);
    const issuesForGate = freshIssues ?? issues;
    // Sync stale context so the UI reflects current DB state
    if (freshIssues) setIssues(freshIssues);

    // ── SCPS Closeout Gate (unified) ──
    const closeoutGate = evaluateCloseoutGate(job, issuesForGate);
    if (!closeoutGate.canClose) {
      toast.error(`Cannot close — ${closeoutGate.blockers[0]}`, {
        description: closeoutGate.blockers.length > 1 ? `+${closeoutGate.blockers.length - 1} more blocker(s)` : undefined,
      });
      return;
    }

    // Financial integrity gate
    const integrity = checkFinancialIntegrity(job);
    if (!integrity.valid) {
      toast.error(`Financial discrepancy: ${integrity.discrepancies[0]}`);
      return;
    }
    // Wrap record gate — prefer wrap-driven validation, but allow DB QA-backed
    // closeout when wrap records are absent.
    const jobWraps = installerWrapRecords.filter(w => w.jobId === job.id);
    if (jobWraps.length === 0) {
      const hasQaEvidence =
        job.qaSubmitted === true ||
        job.qaStatus === "Submitted" ||
        job.qaStatus === "Passed";
      if (!hasQaEvidence) {
        toast.error("Cannot approve — no installer wrap record exists for this job");
        return;
      }
    }
    if (jobWraps.length > 0) {
      const latestWrap = jobWraps.sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0];
      // Payment resolution gate
      if (latestWrap.closeoutEligible && !latestWrap.paymentCapture) {
        toast.error("Cannot approve — payment resolution not addressed in wrap");
        return;
      }
    }
    // Validate status writes
    const checks = [
      validateStatusWrite("qa_closeout", "closeoutStatus", "Closed"),
      validateStatusWrite("qa_closeout", "currentStage", "Completed"),
      validateStatusWrite("qa_closeout", "qaStatus", "Passed"),
    ];
    checks.forEach(c => { if (!c.valid) console.warn("[QA/Closeout] Blocked write:", c.violation); });
    const { data: persistedJob, error: persistError } = await CloseoutService.approve(
      job.id,
      checklist as ReviewerChecklist,
      notes || "QA approved and closed",
      closeoutSignoffState.valid,
    );
    if (persistError || !persistedJob) {
      toast.error("Failed to persist closeout in DB");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === job.id ? persistedJob : j)));
    localStorage.removeItem(closeoutDraftKey);
    await onMutated?.();
    toast.success("Job approved and closed — review flywheel activated");
    onOpenChange(false);
  };

  const handleSatisfactionResponse = async (satisfied: boolean) => {
    const { data: updatedJob, error } = await CloseoutService.recordSatisfaction(job.id, satisfied);
    if (error || !updatedJob) {
      toast.error("Failed to persist satisfaction response");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === job.id ? updatedJob : j)));
    await onMutated?.();

    if (satisfied) {
      toast.success("Review request sent to customer via SMS and email");
    } else {
      // issue creation is persisted by CloseoutService.recordSatisfaction(false)
      toast.warning("Internal issue created — review link NOT sent to protect brand reputation");
    }
  };

  const handleMarkReviewCompleted = async () => {
    const { data: updatedJob, error } = await CloseoutService.markReviewCompleted(job.id);
    if (error || !updatedJob) {
      toast.error("Failed to persist review completion");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === job.id ? updatedJob : j)));
    await onMutated?.();
    toast.success("Review marked as completed");
  };

  const handleSendReferralRequest = async () => {
    const { data: updatedJob, error } = await CloseoutService.sendReferralRequest(job.id);
    if (error || !updatedJob) {
      toast.error("Failed to persist referral request");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === job.id ? updatedJob : j)));
    await onMutated?.();
    toast.success("Referral request sent to customer");
  };

  const handleMarkReferralReceived = async () => {
    const { data: updatedJob, error } = await CloseoutService.markReferralReceived(job.id);
    if (error || !updatedJob) {
      toast.error("Failed to persist referral receipt");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === job.id ? updatedJob : j)));
    await onMutated?.();
    toast.success("Referral marked as received");
  };

  const handleSendContractorRatingRequest = async () => {
    const { data: updatedJob, error } = await CloseoutService.sendContractorRatingRequest(job.id);
    if (error || !updatedJob) {
      toast.error("Failed to send contractor rating request");
      return;
    }
    // Check if email specifically failed (DB may have updated but email didn't send)
    const emailFailed = (error as Error | null)?.message?.includes("email failed to send");
    setJobs(prev => prev.map(j => (j.id === job.id ? updatedJob : j)));
    await onMutated?.();
    if (emailFailed) {
      toast.error("Rating record saved, but the email notification failed to send");
    } else {
      toast.success("Contractor rating request sent to customer");
    }
  };

  const handleMarkContractorRatingCompleted = async () => {
    const { data: updatedJob, error } = await CloseoutService.markContractorRatingCompleted(job.id);
    if (error || !updatedJob) {
      toast.error("Failed to mark contractor rating completed");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === job.id ? updatedJob : j)));
    await onMutated?.();
    toast.success("Contractor rating marked as completed");
  };

  const handleSendBack = async () => {
    const check = validateStatusWrite("qa_closeout", "closeoutStatus", "Install Complete");
    if (!check.valid) console.warn("[QA/Closeout] Blocked write:", check.violation);
    const { data: updatedJob, error } = await CloseoutService.sendBack(job.id, notes || "Sent back to Ops for resolution");
    if (error || !updatedJob) {
      toast.error("Failed to persist send-back action");
      return;
    }
    setJobs(prev => prev.map(j => (j.id === job.id ? updatedJob : j)));
    localStorage.removeItem(closeoutDraftKey);
    await onMutated?.();
    toast.warning("Job sent back to Operations for resolution");
    onOpenChange(false);
  };

  const handleCreateServiceRequest = async () => {
    if (!serviceType || !serviceNotes.trim()) {
      toast.error("Please select a type and add details");
      return;
    }
    const { data: createdIssue, error } = await CloseoutService.createServiceRequest(
      job.id,
      serviceType,
      serviceNotes.trim(),
      "QA Inspector",
    );
    if (error || !createdIssue) {
      toast.error("Failed to persist service request");
      return;
    }
    setIssues(prev => [...prev, createdIssue]);
    await onMutated?.();
    setShowServiceDialog(false);
    setServiceType("");
    setServiceNotes("");
    toast.success("Service request created and linked to job");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              QA Review — {getJobDisplayRef(job)}
              <Badge variant="outline" className="text-[10px] ml-auto">{job.customerName}</Badge>
            </DialogTitle>
          </DialogHeader>

          {/* Revenue Summary — shown for completed/closed jobs */}
          {(job.closeoutStatus === "Closed" || job.closeoutStatus === "Ready for Closeout") && job.financials?.contractValue && (
            <div className="rounded-lg border bg-success/5 border-success/15 px-4 py-3 flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-success dark:text-success shrink-0" />
              <div>
                <p className="text-sm font-bold text-success dark:text-success">
                  This job generated ${job.financials.contractValue.toLocaleString()} in revenue
                </p>
                {job.financials.estimatedMaterialCost != null && (
                  <p className="text-[11px] text-success/70 dark:text-success/70 mt-0.5">
                    Est. profit: ${(job.financials.contractValue - (job.financials.estimatedMaterialCost ?? 0) - (job.financials.estimatedLaborCost ?? 0) - (job.financials.subcontractorPayout ?? 0)).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-5">
            {/* QA Decision Summary Card */}
            {(() => {
              const state = deriveJobState(job, issues, installerWrapRecords);
              const integrity = checkFinancialIntegrity(job);
              const closeoutGate = evaluateCloseoutGate(job, issues);
              const canApprove = state.canProgress && integrity.valid && closeoutGate.canClose && openIssues.length === 0;
              return (
                <div className={`rounded-lg border-2 px-4 py-3 ${
                  canApprove ? "border-success/40 bg-success/5" : "border-warning/40 bg-warning/5"
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {canApprove
                      ? <CheckCircle className="h-5 w-5 text-success shrink-0" />
                      : <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
                    }
                    <p className={`text-sm font-bold ${canApprove ? "text-success" : "text-warning"}`}>
                      {canApprove ? "Ready to Approve" : "Review Required"}
                    </p>
                    <Badge variant="outline" className="ml-auto text-[9px]">{state.label}</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px]">
                    <div className="flex items-center gap-1.5">
                      {openIssues.length === 0
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                        : <XCircle className="h-3.5 w-3.5 text-destructive" />}
                      <span>{openIssues.length === 0 ? "No issues" : `${openIssues.length} open issue(s)`}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {collection.paymentStatus === "paid_in_full"
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                        : collection.paymentStatus === "overdue"
                          ? <XCircle className="h-3.5 w-3.5 text-destructive" />
                          : <Clock className="h-3.5 w-3.5 text-warning" />}
                      <span>{getCollectionPaymentLabel(collection.paymentStatus)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {integrity.valid
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                        : <XCircle className="h-3.5 w-3.5 text-destructive" />}
                      <span>{integrity.valid ? "Financials valid" : "Discrepancy"}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {job.qaSubmitted
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                        : <Clock className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span>{job.qaSubmitted ? "QA submitted" : "QA pending"}</span>
                    </div>
                  </div>
                  {!integrity.valid && (
                    <div className="mt-2 text-[10px] text-destructive">
                      {integrity.discrepancies.map((d, i) => <p key={i}>⚠ {d}</p>)}
                    </div>
                  )}
                  {state.blockers.length > 0 && (
                    <div className="mt-2 text-[10px] text-warning">
                      {state.blockers.map((b, i) => <p key={i}>⚠ {b}</p>)}
                    </div>
                  )}
                  {!closeoutGate.canClose && (
                    <div className="mt-2 text-[10px] text-warning">
                      {closeoutGate.blockers.map((b, i) => <p key={`gate-${i}`}>⚠ {b}</p>)}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Overview strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <InfoCard label="Project" value={job.projectType} />
              <InfoCard label="Installer" value={getInstallerDisplayName(job)} />
              <InfoCard label="Install Date" value={formatShortDate(job.scheduledInstallDate) || "—"} />
              <InfoCard label="Open Issues" value={String(openIssues.length)} highlight={openIssues.length > 0} />
            </div>

             <Tabs defaultValue="field" className="space-y-3">
              <TabsList className="w-full grid grid-cols-6">
                <TabsTrigger value="field" className="text-xs gap-1"><Sunset className="h-3 w-3" />Field</TabsTrigger>
                <TabsTrigger value="photos" className="text-xs gap-1"><Image className="h-3 w-3" />Photos</TabsTrigger>
                <TabsTrigger value="scope" className="text-xs gap-1"><Columns2 className="h-3 w-3" />Scope</TabsTrigger>
                <TabsTrigger value="checklist" className="text-xs gap-1"><ListChecks className="h-3 w-3" />QA Check</TabsTrigger>
                <TabsTrigger value="issues" className="text-xs gap-1"><AlertTriangle className="h-3 w-3" />Issues</TabsTrigger>
                <TabsTrigger value="review" className="text-xs gap-1"><Star className="h-3 w-3" />Review</TabsTrigger>
              </TabsList>

              {/* Field Report — Installer Wrap Context */}
              <TabsContent value="field" className="space-y-3">
                <FieldWrapContext jobId={job.id} job={job} wrapRecords={installerWrapRecords} />
              </TabsContent>

              {/* A. Photo Review */}
              <TabsContent value="photos" className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Reviewing existing photos uploaded during installation. Phase Completion proofs are shown first, matching the photo evidence used in Job Detail.
                </p>
                {executionProofPhotoUrls.length > 0 ? (
                  <div className="space-y-3">
                    <div className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold">Phase Completion Proofs</p>
                        <Badge variant="default" className="text-[10px]">
                          {executionProofPhotoUrls.length} photo(s)
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {executionProofPhotoUrls.map((p, i) => (
                          <a key={`${p}-${i}`} href={p} target="_blank" rel="noreferrer" className="aspect-square rounded-md border bg-muted/30 overflow-hidden block">
                            <StorageThumbnailImage
                              src={p}
                              alt={`Phase completion proof ${i + 1}`}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = "none";
                                (e.currentTarget.parentElement as HTMLElement).classList.add("flex", "items-center", "justify-center");
                                (e.currentTarget.parentElement as HTMLElement).innerHTML = '<svg class="h-5 w-5 opacity-40 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>';
                              }}
                            />
                          </a>
                        ))}
                      </div>
                    </div>

                    {supplementalPhotoUrls.length > 0 && (
                      <div className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-bold">Supplemental Evidence</p>
                          <Badge variant="secondary" className="text-[10px]">
                            {supplementalPhotoUrls.length} photo(s)
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {supplementalPhotoUrls.map((p, i) => (
                            <a key={`${p}-${i}`} href={p} target="_blank" rel="noreferrer" className="aspect-square rounded-md border bg-muted/30 overflow-hidden block">
                              <StorageThumbnailImage
                                src={p}
                                alt={`Supplemental photo ${i + 1}`}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  (e.currentTarget as HTMLImageElement).style.display = "none";
                                  (e.currentTarget.parentElement as HTMLElement).classList.add("flex", "items-center", "justify-center");
                                  (e.currentTarget.parentElement as HTMLElement).innerHTML = '<svg class="h-5 w-5 opacity-40 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>';
                                }}
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : photoCategories.total === 0 ? (
                  <div className="py-8 text-center border rounded-lg bg-muted/20">
                    <Camera className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">No photos uploaded for this job</p>
                    <p className="text-[10px] text-destructive mt-1">Photo documentation incomplete</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(["before", "during", "after"] as const).map(phase => (
                      <div key={phase} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-bold capitalize">{phase} Photos</p>
                          <Badge variant={photoCategories[phase].length > 0 ? "default" : "secondary"} className="text-[10px]">
                            {photoCategories[phase].length} photo(s)
                          </Badge>
                        </div>
                        {photoCategories[phase].length > 0 ? (
                          <div className="grid grid-cols-3 gap-2">
                            {photoCategories[phase].map((p, i) => (
                              <a key={i} href={p} target="_blank" rel="noreferrer" className="aspect-square rounded-md border bg-muted/30 overflow-hidden block">
                                <StorageThumbnailImage
                                  src={p}
                                  alt={`${phase} photo ${i + 1}`}
                                  className="h-full w-full object-cover"
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).style.display = "none";
                                    (e.currentTarget.parentElement as HTMLElement).classList.add("flex", "items-center", "justify-center");
                                    (e.currentTarget.parentElement as HTMLElement).innerHTML = '<svg class="h-5 w-5 opacity-40 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>';
                                  }}
                                />
                              </a>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[10px] text-muted-foreground">No {phase} photos</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* B. Scope Verification */}
              <TabsContent value="scope" className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Side-by-side comparison of sold scope vs installed scope.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="border rounded-lg p-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                      <FileCheck className="h-3 w-3" /> Sold Scope (Work Order)
                    </p>
                    {soldScope.sections.map((section, i) => (
                      <div key={i} className="mb-2">
                        <p className="text-xs font-semibold">{section.title}</p>
                        <ul className="space-y-0.5 mt-0.5">
                          {section.items.map((item, j) => (
                            <li key={j} className="text-[11px] text-muted-foreground flex items-start gap-1.5">
                              <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0 text-primary/40" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <div className="border rounded-lg p-3">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Wrench className="h-3 w-3" /> Installed Scope (Confirmed)
                    </p>
                    {job.qaSubmitted ? (
                      soldScope.sections.map((section, i) => (
                        <div key={i} className="mb-2">
                          <p className="text-xs font-semibold">{section.title}</p>
                          <ul className="space-y-0.5 mt-0.5">
                            {section.items.map((item, j) => (
                              <li key={j} className="text-[11px] text-success dark:text-success flex items-start gap-1.5">
                                <CheckCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))
                    ) : (
                      <div className="py-6 text-center">
                        <Clock className="h-6 w-6 text-muted-foreground/40 mx-auto mb-1" />
                        <p className="text-[11px] text-muted-foreground">Awaiting installer confirmation</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* C. QA Checklist */}
              <TabsContent value="checklist" className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Verify each item before approving. All checks are required.
                </p>
                <div className="space-y-2">
                  {QA_ITEMS.map(item => (
                    <label
                      key={item.key}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checklist[item.key]
                          ? "border-success/40 bg-success/50 dark:border-success/70 dark:bg-success/20"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <Checkbox
                        checked={!!checklist[item.key]}
                        onCheckedChange={(checked) =>
                          setChecklist(prev => ({ ...prev, [item.key]: !!checked }))
                        }
                      />
                      <span className="text-xs font-medium">{item.label}</span>
                      {checklist[item.key] && <CheckCircle2 className="h-4 w-4 text-success ml-auto" />}
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <p className="text-[10px] text-muted-foreground">
                    {Object.values(checklist).filter(Boolean).length}/{QA_ITEMS.length} verified
                  </p>
                  <Progress
                    value={(Object.values(checklist).filter(Boolean).length / QA_ITEMS.length) * 100}
                    className="w-32 h-1.5"
                  />
                </div>
              </TabsContent>

              {/* Issues Tab — only shows unresolved issues */}
              <TabsContent value="issues" className="space-y-3">
                {openIssues.length === 0 ? (
                  <div className="py-8 text-center border rounded-lg bg-muted/20">
                    <CheckCircle className="h-8 w-8 text-success/40 mx-auto mb-2" />
                    <p className="text-xs font-medium text-success">All issues resolved</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Job is clear for approval</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {openIssues.map(iss => (
                      <div key={iss.id} className="flex items-start gap-3 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
                        <div className={`shrink-0 h-2 w-2 rounded-full mt-1.5 ${
                          iss.severity === "Urgent" ? "bg-destructive" : iss.severity === "Medium" ? "bg-warning/60" : "bg-muted-foreground"
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">{iss.description}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {iss.issueType} · {iss.severity} · {iss.status}
                          </p>
                        </div>
                        <Badge variant="destructive" className="text-[10px] shrink-0">
                          {iss.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                <PermissionGate permission="issues_create">
                  <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setShowServiceDialog(true)}>
                    <Plus className="h-3 w-3 mr-1" /> Create Service Request
                  </Button>
                </PermissionGate>
              </TabsContent>

              {/* E. Review & Referral Flywheel */}
              <TabsContent value="review" className="space-y-4">
                {/* Satisfaction Gate */}
                {job.closeoutStatus === "Closed" && job.reviewSatisfied === null && (
                  <div className="border rounded-lg p-4 space-y-3 bg-primary/5 border-primary/20">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-primary" />
                      <p className="text-xs font-bold">Customer Satisfaction Check</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Before sending a review request, confirm the customer's satisfaction to protect brand reputation.
                    </p>
                    <p className="text-xs font-medium">"Was everything completed to your satisfaction?"</p>
                    <div className="flex gap-2">
                      <Button size="sm" className="flex-1 bg-success hover:bg-success text-white" onClick={() => handleSatisfactionResponse(true)}>
                        <ThumbsUp className="h-3.5 w-3.5 mr-1.5" /> Yes — Send Review Link
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 border-destructive/30 text-destructive hover:bg-destructive/10" onClick={() => handleSatisfactionResponse(false)}>
                        <ThumbsDown className="h-3.5 w-3.5 mr-1.5" /> No — Flag Issue
                      </Button>
                    </div>
                  </div>
                )}

                {/* Review Status */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-warning" />
                    <p className="text-xs font-bold">Review Status</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted/50 rounded-md p-3">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Satisfaction</p>
                      <p className="text-xs font-medium mt-0.5">
                        {job.reviewSatisfied === true ? "✅ Satisfied" :
                         job.reviewSatisfied === false ? "❌ Not Satisfied" :
                         job.closeoutStatus !== "Closed" ? "⏳ Pending Approval" : "⏳ Awaiting Response"}
                      </p>
                    </div>
                    <div className="bg-muted/50 rounded-md p-3">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Review Sent</p>
                      <p className="text-xs font-medium mt-0.5">
                        {job.reviewSent ? `✅ Sent${job.reviewSentAt ? ` · ${formatShortDate(job.reviewSentAt)}` : ""}` : "—"}
                      </p>
                    </div>
                    <div className="bg-muted/50 rounded-md p-3">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Review Completed</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs font-medium">{job.reviewCompleted ? "✅ Completed" : "—"}</p>
                        {job.reviewSent && !job.reviewCompleted && (
                          <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px]" onClick={handleMarkReviewCompleted}>
                            Mark Done
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="bg-muted/50 rounded-md p-3">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Review Link</p>
                      <p className="text-xs font-medium mt-0.5 flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" /> Google Review
                      </p>
                    </div>
                  </div>
                  {job.reviewSatisfied === false && (
                    <div className="border border-destructive/30 rounded-md p-3 bg-destructive/5">
                      <p className="text-xs font-bold text-destructive flex items-center gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" /> Review Blocked
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Customer indicated dissatisfaction. Internal issue created. Review link was NOT sent.
                      </p>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground italic">
                    SMS (primary) and email (backup) sent within 5–10 min of approval.
                    Message: "Hi {job.customerName.split(" ")[0]}, it was a pleasure working on your project..."
                  </p>
                </div>

                {/* Referral Status */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <p className="text-xs font-bold">Referral Status</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-muted/50 rounded-md p-3">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Request Sent</p>
                      <p className="text-xs font-medium mt-0.5">
                        {job.referralRequested ? `✅ Sent${job.referralRequestedAt ? ` · ${formatShortDate(job.referralRequestedAt)}` : ""}` : "—"}
                      </p>
                    </div>
                    <div className="bg-muted/50 rounded-md p-3">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Referral Received</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs font-medium">{job.referralReceived ? "✅ Received" : "—"}</p>
                        {job.referralRequested && !job.referralReceived && (
                          <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px]" onClick={handleMarkReferralReceived}>
                            Mark Received
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
{job.reviewSatisfied === true && !job.referralRequested && (
                     <Button size="sm" variant="outline" className="w-full text-xs" onClick={handleSendReferralRequest}>
                       <Send className="h-3 w-3 mr-1.5" /> Send Referral Request
                     </Button>
                   )}
                   <p className="text-[10px] text-muted-foreground italic">
                     Referral prompt sent 24–48 hours after review completion or automatically after delay.
                   </p>
                 </div>

                 {/* Contractor Rating Status */}
                 <div className="border rounded-lg p-4 space-y-3">
                   <div className="flex items-center gap-2">
                     <Star className="h-4 w-4 text-warning" />
                     <p className="text-xs font-bold">Contractor Rating</p>
                   </div>
                   <div className="grid grid-cols-2 gap-3">
                     <div className="bg-muted/50 rounded-md p-3">
                       <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Rating Sent</p>
                       <p className="text-xs font-medium mt-0.5">
                         {job.contractorRatingSent
                           ? `✅ Sent${job.contractorRatingAt ? ` · ${formatShortDate(job.contractorRatingAt)}` : ""}`
                           : "—"}
                       </p>
                     </div>
                     <div className="bg-muted/50 rounded-md p-3">
                       <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Rating Completed</p>
                       <div className="flex items-center gap-2 mt-0.5">
                         <p className="text-xs font-medium">{job.contractorRatingCompleted ? "✅ Completed" : "—"}</p>
                         {job.contractorRatingSent && !job.contractorRatingCompleted && (
                           <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px]" onClick={handleMarkContractorRatingCompleted}>
                             Mark Completed
                           </Button>
                         )}
                       </div>
                     </div>
                   </div>
                   {job.reviewSatisfied === true && !job.contractorRatingSent && (
                     <Button size="sm" variant="outline" className="w-full text-xs" onClick={handleSendContractorRatingRequest}>
                       <Send className="h-3 w-3 mr-1.5" /> Send Contractor Rating Link
                     </Button>
                   )}
                   <p className="text-[10px] text-muted-foreground italic">
                     Contractor rating link sent to customer after satisfaction confirmation.
                   </p>
                 </div>

                 {job.closeoutStatus !== "Closed" && (
                  <div className="border rounded-md p-3 bg-muted/20">
                    <p className="text-xs text-muted-foreground text-center">
                      Review & referral flywheel activates after QA approval and job closure.
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Notes */}
            <div>
              <p className="text-xs font-medium mb-1.5">QA Notes</p>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add review notes..."
                rows={2}
                className="text-xs"
              />
            </div>

            {/* D. Actions */}
            {openIssues.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-destructive/40 bg-destructive/5 text-destructive text-xs">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {openIssues.length} open issue{openIssues.length > 1 ? "s" : ""} must be resolved before approving this job.
                </span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
              <PermissionGate permission="jobs_approve">
                <Button
                  onClick={handleApprove}
                  disabled={
                    isAlreadyClosed ||
                    !allChecked ||
                    !evaluateCloseoutGate(job, issues).canClose ||
                    !closeoutSignoffState.valid ||
                    openIssues.length > 0
                  }
                  className="flex-1 bg-success hover:bg-success text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-1.5" /> {isAlreadyClosed ? "Already Closed" : "Approve & Close Job"}
                </Button>
              </PermissionGate>
              <PermissionGate permission="jobs_approve">
                <Button variant="outline" onClick={handleSendBack} className="flex-1">
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Send Back to Ops
                </Button>
              </PermissionGate>
              <PermissionGate permission="issues_create">
                <Button variant="outline" onClick={() => setShowServiceDialog(true)} className="flex-1">
                  <Send className="h-4 w-4 mr-1.5" /> Create Service Request
                </Button>
              </PermissionGate>
            </div>

            {openIssues.length > 0 && (
              <div className="border border-warning/40 dark:border-warning/60 rounded-md p-3 bg-warning/50 dark:bg-warning/20">
                <p className="text-xs font-bold text-warning dark:text-warning flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> {openIssues.length} open issue(s)
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Consider resolving all issues before approving, or create a service request for post-close follow-up.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Request Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" /> Create Service Request
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium mb-1.5">Request Type</p>
              <Select value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Punch List">Punch List Task</SelectItem>
                  <SelectItem value="Service Request">Service Request</SelectItem>
                  <SelectItem value="Warranty Claim">Warranty Claim</SelectItem>
                  <SelectItem value="Customer Concern">Customer Concern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-medium mb-1.5">Details</p>
              <Textarea value={serviceNotes} onChange={e => setServiceNotes(e.target.value)} placeholder="Describe the issue..." rows={3} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowServiceDialog(false)} className="flex-1">Cancel</Button>
              <PermissionGate permission="issues_create">
                <Button onClick={handleCreateServiceRequest} className="flex-1">
                  <Plus className="h-4 w-4 mr-1" /> Create Request
                </Button>
              </PermissionGate>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ═══════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════ */

function InfoCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="bg-muted/50 rounded-md p-2.5">
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-xs font-medium ${highlight ? "text-destructive" : ""}`}>{value}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════
   Field Wrap Context — Decision Cockpit
   ═══════════════════════════════════════════ */

function FieldWrapContext({ jobId, job, wrapRecords }: { jobId: string; job: Job; wrapRecords: InstallerWrapRecord[] }) {
  const [showHistory, setShowHistory] = useState(false);

  const jobWraps = useMemo(() =>
    wrapRecords
      .filter(r => r.jobId === jobId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [wrapRecords, jobId],
  );

  const clearance = useMemo(() => deriveFinancialClearance(job), [job]);

  if (jobWraps.length === 0) {
    return (
      <div className="border rounded-lg p-6 bg-muted/20 text-center">
        <Sunset className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
        <p className="text-xs text-muted-foreground">No installer wrap records for this job.</p>
      </div>
    );
  }

  const latest = jobWraps[0];
  const outcomeLabel = OUTCOME_OPTIONS.find(o => o.value === latest.outcome)?.label ?? latest.outcome;
  const settlementCfg = SETTLEMENT_LABELS[latest.settlementStatus];
  const older = jobWraps.slice(1);

  // ── 1. QA Decision Signal ──
  const isBlocked = latest.outcome === "blocked_issue" || latest.settlementStatus === "hold_issue";
  const isIncomplete = !latest.closeoutEligible || latest.completionPercent < 100;
  const isReady = latest.closeoutEligible && latest.completionPercent === 100;
  const rescheduleNeeded = latest.customerComm?.rescheduleNeeded ?? false;

  const signal = isBlocked
    ? { label: "Blocked — Issue Requires Resolution", border: "border-destructive/40", bg: "bg-destructive/5", text: "text-destructive", icon: XCircle }
    : isIncomplete
    ? { label: "Work Incomplete — Follow-Up Required", border: "border-warning/40", bg: "bg-warning/5", text: "text-warning", icon: AlertTriangle }
    : { label: "Ready for Closeout", border: "border-success/40", bg: "bg-success/5", text: "text-success", icon: CheckCircle };

  // ── 3. Customer Expectation Signal ──
  const custInformed = latest.customerComm?.customerInformed ?? false;
  const custSummary = custInformed
    ? rescheduleNeeded
      ? `Customer informed and aligned — return visit ${latest.customerComm?.proposedNextDate ? `scheduled for ${latest.customerComm.proposedNextDate}` : "required"}`
      : "Customer informed and aligned with next steps"
    : "Customer NOT informed — risk of friction";

  // ── Financial context ──
  const clearanceLabel = clearance.status === "cleared" ? "Financially Cleared" : clearance.status === "pending" ? "Financial Clearance Pending" : "Financial Clearance Blocked";
  const clearanceColor = clearance.status === "cleared" ? "text-success" : clearance.status === "pending" ? "text-warning" : "text-destructive";
  const clearanceBorder = clearance.status === "cleared" ? "border-success/30" : clearance.status === "pending" ? "border-warning/30" : "border-destructive/30";
  const clearanceBg = clearance.status === "cleared" ? "bg-success/5" : clearance.status === "pending" ? "bg-warning/5" : "bg-destructive/5";

  return (
    <div className="space-y-3">

      {/* ═══ 1. QA DECISION SIGNAL ═══ */}
      <div className={`rounded-lg border-2 ${signal.border} ${signal.bg} px-4 py-3`}>
        <div className="flex items-center gap-2.5">
          <signal.icon className={`h-5 w-5 ${signal.text} shrink-0`} />
          <div className="flex-1">
            <p className={`text-sm font-bold ${signal.text}`}>{signal.label}</p>
            {rescheduleNeeded && (
              <Badge variant="outline" className="mt-1 text-[10px] border-warning/40 text-warning">
                Return Visit {latest.customerComm?.proposedNextDate ? `Scheduled: ${latest.customerComm.proposedNextDate}` : "Required"}
              </Badge>
            )}
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] text-muted-foreground">Completion</p>
            <p className={`text-lg font-bold tabular-nums ${signal.text}`}>{latest.completionPercent}%</p>
          </div>
        </div>
      </div>

      {/* ═══ 2. FINANCIAL STATUS ═══ */}
      <div className={`rounded-lg border ${clearanceBorder} ${clearanceBg} px-4 py-2.5`}>
        <div className="flex items-center gap-2">
          <DollarSign className={`h-4 w-4 shrink-0 ${clearanceColor}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold ${clearanceColor}`}>{clearanceLabel}</p>
            <p className="text-[11px] text-muted-foreground truncate">{clearance.reason}</p>
          </div>
          <Badge variant="outline" className={`text-[9px] shrink-0 ${settlementCfg.color}`}>
            {settlementCfg.label}
          </Badge>
        </div>
      </div>

      {/* ═══ 3. COMPLETION + SETTLEMENT ═══ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-muted/50 rounded-md p-2.5">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Outcome</p>
          <p className="text-xs font-semibold">{outcomeLabel}</p>
        </div>
        <div className="bg-muted/50 rounded-md p-2.5">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Completion</p>
          <div className="flex items-center gap-2 mt-0.5">
            <Progress value={latest.completionPercent} className="h-1.5 flex-1" />
            <span className="text-xs font-semibold">{latest.completionPercent}%</span>
          </div>
        </div>
        <div className="bg-muted/50 rounded-md p-2.5">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Closeout Eligible</p>
          <p className="text-xs font-semibold flex items-center gap-1">
            {latest.closeoutEligible
              ? <><CheckCircle className="h-3 w-3 text-success" /> Yes</>
              : <><XCircle className="h-3 w-3 text-destructive" /> No</>
            }
          </p>
        </div>
        <div className="bg-muted/50 rounded-md p-2.5">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Reported</p>
          <p className="text-[10px] text-muted-foreground">
            {new Date(latest.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </p>
        </div>
      </div>

      {/* ═══ 3b. SERVICE CHECKLIST (if present) ═══ */}
      {latest.serviceChecklist && (
        <div className="rounded-md border border-border/40 bg-muted/20 px-3 py-2.5">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5">Service Prevention Checklist</p>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              { key: "visualInspection" as const, label: "Visual Inspection" },
              { key: "functionalityConfirmed" as const, label: "Functionality" },
              { key: "materialsInstalledCorrectly" as const, label: "Materials Correct" },
              { key: "customerWalkthrough" as const, label: "Customer Walkthrough" },
            ].map(item => (
              <div key={item.key} className="flex items-center gap-1.5 text-[11px]">
                {latest.serviceChecklist![item.key]
                  ? <CheckCircle className="h-3 w-3 text-success shrink-0" />
                  : <XCircle className="h-3 w-3 text-destructive shrink-0" />
                }
                <span className={latest.serviceChecklist![item.key] ? "text-foreground" : "text-destructive"}>{item.label}</span>
              </div>
            ))}
          </div>
          {latest.serviceChecklist.overridden && (
            <div className="mt-1.5 space-y-1">
              <Badge variant="outline" className="text-[9px] border-destructive/40 text-destructive">
                ⚠ High Risk Install — Checklist Overridden
              </Badge>
              {latest.serviceChecklist.overrideReason && (
                <p className="text-[10px] text-muted-foreground ml-0.5">
                  Reason: {latest.serviceChecklist.overrideReason}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ 3c. PAYMENT STATUS (if present) ═══ */}
      {latest.paymentCapture && (
        <div className={`rounded-md border px-3 py-2.5 ${
          latest.paymentCapture.disposition === "collected"
            ? "border-success/30 bg-success/5"
            : "border-warning/30 bg-warning/5"
        }`}>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Payment at Completion</p>
          <div className="flex items-center gap-2">
            {latest.paymentCapture.disposition === "collected"
              ? <CheckCircle className="h-3.5 w-3.5 text-success shrink-0" />
              : <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
            }
            <p className={`text-xs font-medium ${latest.paymentCapture.disposition === "collected" ? "text-success" : "text-warning"}`}>
              {latest.paymentCapture.disposition === "collected" ? "Payment Collected" : "Payment Deferred"}
            </p>
          </div>
          {latest.paymentCapture.disposition === "deferred" && latest.paymentCapture.deferralReason && (
            <p className="text-[11px] text-muted-foreground mt-1 ml-5.5">
              Reason: {latest.paymentCapture.deferralReason.replace(/_/g, " ")}
              {latest.paymentCapture.deferralNotes ? ` — ${latest.paymentCapture.deferralNotes}` : ""}
            </p>
          )}
        </div>
      )}

      {/* ═══ 4. INSTALLER NOTES ═══ */}
      {latest.progressNotes && (
        <div className="rounded-md border border-border/40 bg-muted/20 px-3 py-2.5">
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Installer Notes</p>
          <p className="text-xs text-foreground leading-relaxed">{latest.progressNotes}</p>
        </div>
      )}

      {/* ═══ 5. CUSTOMER COMMUNICATION ═══ */}
      <div className={`rounded-md border px-3 py-2.5 ${
        custInformed ? "border-border/40 bg-muted/20" : "border-destructive/30 bg-destructive/5"
      }`}>
        <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1.5">Customer Expectation</p>
        <div className="flex items-center gap-2 mb-1.5">
          {custInformed
            ? <CheckCircle className="h-3.5 w-3.5 text-success shrink-0" />
            : <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
          }
          <p className={`text-xs font-medium ${custInformed ? "text-foreground" : "text-destructive"}`}>
            {custSummary}
          </p>
        </div>
        {latest.customerComm?.communicationSummary && (
          <p className="text-[11px] text-muted-foreground ml-5.5">{latest.customerComm.communicationSummary}</p>
        )}
      </div>

      {/* ═══ 6. WRAP HISTORY ═══ */}
      {older.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {older.length} previous wrap{older.length > 1 ? "s" : ""}
          </button>

          {showHistory && (
            <div className="mt-2 space-y-2">
              {older.map(wrap => {
                const label = OUTCOME_OPTIONS.find(o => o.value === wrap.outcome)?.label ?? wrap.outcome;
                return (
                  <div key={wrap.id} className="rounded-md border border-border/40 bg-muted/20 px-3 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{label} — {wrap.completionPercent}%</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(wrap.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </span>
                    </div>
                    {wrap.progressNotes && (
                      <p className="text-[11px] text-muted-foreground">{wrap.progressNotes}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
